/**
 * QuotesTableV2 — Quotes list body, ported from the prototype's
 * `.screen-quotes` (cms-redesign-mockup.html ~7328+, styles
 * 3260–3324).
 *
 * Layout: `.quotes-grid` — repeat(auto-fill, minmax(280px, 1fr)) of
 * cards. Each card:
 *   • A large decorative open-quote glyph in faded teal at top-left
 *     (CSS pseudo-element in the prototype; here a real span so
 *     React can render it without extra plumbing).
 *   • `.quote-text` — 5-line clamped quote_text.
 *   • Footer divider, then attribution: speaker name + hover-revealed
 *     edit affordance.
 *
 * No drag-to-reorder (the API exposes no sort_order on quotes).
 * Client-side search over quote_text + quote_speaker_name.
 *
 * Data: reuses the v1 `fetchAllQuotes` action — GET /quotes returns
 * `{objects, count}` with `{quote_id, quote_text, quote_speaker_name,
 * quote_speaker_title}` per row.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchAllQuotes } from '../../../../actions/quoteActions';
import { cn } from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

/* ─── Card ─────────────────────────────────────────────────────────── */

function QuoteCard({ q }) {
  return (
    <Link
      to={`/quotes/edit/${q.quote_id}`}
      className={cn(
        'group flex flex-col bg-surface border border-sand no-underline',
        'transition-all duration-200 hover:border-teal-green',
        'relative',
      )}
      style={{
        color: 'inherit',
        borderRadius: 12,
        padding: '22px 22px 16px',
        minHeight: 180,
        minWidth: 0,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow =
          '0 10px 24px #2C3E5012, 0 2px 6px #2C3E500A';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Decorative open-quote glyph in faded teal — gives every card
       *  the same recognizable mark from the prototype. */}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          top: -6,
          left: 14,
          fontSize: 64,
          lineHeight: 1,
          color: '#75A293',
          opacity: 0.18,
          fontWeight: 700,
          pointerEvents: 'none',
        }}
      >
        “
      </span>

      <p
        className="text-navy"
        style={{
          margin: 0,
          fontSize: 15,
          lineHeight: 1.55,
          flex: 1,
          marginBottom: 14,
          display: '-webkit-box',
          WebkitLineClamp: 5,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          position: 'relative', // sit above the decorative quote mark
        }}
      >
        {q.quote_text || 'Untitled'}
      </p>

      <div
        className="flex items-center border-t border-line"
        style={{ gap: 8, paddingTop: 12, fontSize: 12.5 }}
      >
        <span className="text-text-muted truncate min-w-0">
          {q.quote_speaker_name ? (
            <strong className="text-navy" style={{ fontWeight: 600 }}>
              {q.quote_speaker_name}
            </strong>
          ) : (
            <em>Anonymous</em>
          )}
          {q.quote_speaker_title && (
            <span className="text-text-muted" style={{ marginLeft: 6 }}>
              · {q.quote_speaker_title}
            </span>
          )}
        </span>
        <span
          aria-hidden
          className={cn(
            'grid place-items-center text-text-muted ml-auto',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-150',
            'group-hover:text-navy group-hover:bg-cream',
          )}
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            border: '1px solid transparent',
            flexShrink: 0,
          }}
        >
          {EditIconSvg}
        </span>
      </div>
    </Link>
  );
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function QuotesTableV2() {
  const dispatch = useDispatch();
  const items = useSelector((s) => s.quotes.all_quotes);

  useEffect(() => {
    dispatch(fetchAllQuotes());
  }, [dispatch]);

  // Search lives in the global ⌘K palette; no in-page search input.
  const visible = Array.isArray(items) ? items : null;

  if (!Array.isArray(items)) {
    return (
      <div
        className="flex items-center justify-center text-text-muted"
        style={{ height: 256 }}
      >
        Loading quotes…
      </div>
    );
  }

  return (
    <div>
      {visible.length === 0 ? (
        <div
          className="bg-surface border border-sand text-text-muted italic text-center"
          style={{
            borderRadius: 12,
            padding: '40px 16px',
            fontSize: 13,
            margin: '0 28px 32px',
          }}
        >
          No quotes yet — add one to get started.
        </div>
      ) : (
        <div style={{ padding: '0 28px 32px' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gridAutoRows: 'max-content',
              gap: 16,
              alignContent: 'start',
            }}
          >
            {visible.map((q) => (
              <QuoteCard key={q.quote_id} q={q} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
